import React, { useState, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ArrowLeftIcon,
  DocumentChartBarIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';

const API_BASE = '';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatPercent = (value: number) =>
  `${(value * 100).toFixed(1)}%`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

type ReportType = 'ar-aging' | 'wip-aging' | 'expense-recovery' | 'realization' | 'utilization';

const REPORT_OPTIONS: { value: ReportType; label: string; description: string }[] = [
  { value: 'ar-aging', label: 'AR Aging', description: 'Outstanding receivables bucketed by age (0-30, 31-60, 61-90, 91-120, 120+ days)' },
  { value: 'wip-aging', label: 'WIP Aging', description: 'Unbilled work-in-progress by matter, bucketed by age' },
  { value: 'expense-recovery', label: 'Expense Recovery', description: 'Client costs by UTBMS code, matter, and billing or prebill state' },
  { value: 'realization', label: 'Realization', description: 'Billing efficiency — standard vs billed vs collected value' },
  { value: 'utilization', label: 'Utilization', description: 'Attorney productivity — billable vs target hours' },
];

const AGING_BUCKETS = [
  { key: 'current', label: 'Current (0-30)' },
  { key: 'days_31_60', label: '31-60 Days' },
  { key: 'days_61_90', label: '61-90 Days' },
  { key: 'days_91_120', label: '91-120 Days' },
  { key: 'days_120_plus', label: '120+ Days' },
] as const;

const getBucketColor = (key: string) => {
  switch (key) {
    case 'current': return 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    case 'days_31_60': return 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
    case 'days_61_90': return 'text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20';
    case 'days_91_120': return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
    case 'days_120_plus': return 'text-red-800 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
    default: return 'text-gray-700 dark:text-gray-300';
  }
};

interface BillingReportPrintPayload {
  reportType: ReportType;
  reportLabel: string;
  reportData: any;
  generatedAt: string;
  generatedBy: string;
  reportPeriod: string;
  digest: string;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value as Record<string, unknown>).sort().map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`).join(',')}}`;
}

async function sha256Hex(value: string): Promise<string> {
  if (window.crypto?.subtle) {
    const bytes = new TextEncoder().encode(value);
    const hash = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  return `fallback-${Math.abs(hash).toString(16)}`;
}

function reportLabelFor(type: ReportType): string {
  return REPORT_OPTIONS.find((option) => option.value === type)?.label || 'Billing Report';
}

function reportPeriodFor(type: ReportType, data: any): string {
  if (type === 'ar-aging' || type === 'wip-aging') {
    return `As of ${formatDate(data?.as_of_date)}`;
  }
  return `${formatDate(data?.date_from)} - ${formatDate(data?.date_to)}`;
}

const BillingReportsPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();

  const [reportType, setReportType] = useState<ReportType>('ar-aging');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [targetHours, setTargetHours] = useState('160');

  const [reportData, setReportData] = useState<any>(null);
  const [printPayload, setPrintPayload] = useState<BillingReportPrintPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      let url = `${API_BASE}/api/v1/billing/reports/${reportType}`;
      const params = new URLSearchParams();

      if (reportType === 'ar-aging' || reportType === 'wip-aging') {
        params.set('as_of_date', asOfDate);
      } else {
        params.set('date_from', dateFrom);
        params.set('date_to', dateTo);
        if (reportType === 'utilization') {
          params.set('target_hours', targetHours);
        }
      }

      url += `?${params.toString()}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.description || body?.message || `Request failed (${res.status})`);
      }
      const json = await res.json();
      setReportData(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const needsDateRange = reportType === 'expense-recovery' || reportType === 'realization' || reportType === 'utilization';

  const printOfficialReport = async () => {
    if (!reportData) return;
    const generatedAt = new Date().toISOString();
    const tokenParsed = keycloak?.tokenParsed as Record<string, any> | undefined;
    const generatedBy = tokenParsed?.name || tokenParsed?.preferred_username || tokenParsed?.email || 'EdgeLex user';
    const reportLabel = reportLabelFor(reportType);
    const reportPeriod = reportPeriodFor(reportType, reportData);
    const digest = await sha256Hex(stableStringify({
      reportType,
      reportPeriod,
      generatedAt,
      reportData,
    }));

    const payload = { reportType, reportLabel, reportData, generatedAt, generatedBy, reportPeriod, digest };
    setPrintPayload(payload);
    document.body.classList.add('billing-report-print-active');

    const cleanup = () => {
      document.body.classList.remove('billing-report-print-active');
      setPrintPayload(null);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        window.print();
      }, 80);
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/billing')} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Billing Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Financial analytics and aging analysis</p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        {/* Report type selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {REPORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setReportType(opt.value); setReportData(null); }}
              className={`text-left p-3 rounded-lg border transition-colors ${
                reportType === opt.value
                  ? 'border-lex-accent bg-lex-accent-soft ring-1 ring-lex-accent'
                  : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className={`text-sm font-medium ${reportType === opt.value ? 'text-lex-accent' : 'text-gray-900 dark:text-white'}`}>
                {opt.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>

        {/* Date controls */}
        <div className="flex flex-wrap items-end gap-3">
          {!needsDateRange ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">As of Date</label>
              <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              {reportType === 'utilization' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Target Hrs/Month</label>
                  <input type="number" min="1" step="1" value={targetHours} onChange={(e) => setTargetHours(e.target.value)}
                    className="w-24 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
              )}
            </>
          )}
          <button
            onClick={generateReport}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
          >
            <DocumentChartBarIcon className="h-4 w-4" />
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {reportData && (
            <button onClick={printOfficialReport} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <PrinterIcon className="h-4 w-4" />
              Print
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Report content */}
      {reportData && reportType === 'ar-aging' && <ArAgingReportView data={reportData} />}
      {reportData && reportType === 'wip-aging' && <WipAgingReportView data={reportData} />}
      {reportData && reportType === 'expense-recovery' && <ExpenseRecoveryReportView data={reportData} />}
      {reportData && reportType === 'realization' && <RealizationReportView data={reportData} />}
      {reportData && reportType === 'utilization' && <UtilizationReportView data={reportData} />}
      <BillingReportPrintStyles />
      {printPayload && <OfficialBillingReportDocument payload={printPayload} />}
    </div>
  );
};

// ── Official Billing Report Print Engine ──

const BillingReportPrintStyles: React.FC = () => (
  <style>{`
    [data-billing-report-print-document] {
      display: none;
    }

    @media print {
      @page {
        margin: 0.55in;
        size: auto;
      }

      body.billing-report-print-active {
        background: #ffffff !important;
      }

      body.billing-report-print-active * {
        box-shadow: none !important;
        overflow: visible !important;
        scrollbar-width: none !important;
        visibility: hidden !important;
      }

      body.billing-report-print-active *::-webkit-scrollbar {
        display: none !important;
      }

      body.billing-report-print-active [data-billing-report-print-document],
      body.billing-report-print-active [data-billing-report-print-document] * {
        visibility: visible !important;
      }

      body.billing-report-print-active [data-billing-report-print-document] {
        background: #ffffff !important;
        color: #111827 !important;
        display: block !important;
        font-family: Arial, Helvetica, sans-serif !important;
        font-size: 9.5pt !important;
        left: 0 !important;
        line-height: 1.35 !important;
        position: absolute !important;
        top: 0 !important;
        width: 100% !important;
      }

      .billing-print-header {
        border-bottom: 2px solid #111827;
        margin-bottom: 16px;
        padding-bottom: 10px;
      }

      .billing-print-kicker {
        color: #4b5563;
        font-size: 8pt;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .billing-print-title {
        font-size: 18pt;
        font-weight: 700;
        margin-top: 3px;
      }

      .billing-print-meta {
        display: grid;
        gap: 4px 18px;
        grid-template-columns: 1fr 1fr;
        margin-top: 10px;
      }

      .billing-print-meta div {
        min-width: 0;
      }

      .billing-print-meta span {
        color: #4b5563;
        display: inline-block;
        font-size: 8pt;
        font-weight: 700;
        min-width: 88px;
        text-transform: uppercase;
      }

      .billing-print-section {
        break-inside: avoid;
        margin-top: 16px;
      }

      .billing-print-section h2 {
        border-bottom: 1px solid #d1d5db;
        font-size: 12pt;
        margin: 0 0 8px;
        padding-bottom: 4px;
      }

      .billing-print-summary {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(3, 1fr);
      }

      .billing-print-card {
        border: 1px solid #d1d5db;
        break-inside: avoid;
        padding: 8px;
      }

      .billing-print-card-label {
        color: #4b5563;
        font-size: 8pt;
        font-weight: 700;
      }

      .billing-print-card-value {
        font-size: 13pt;
        font-weight: 700;
        margin-top: 2px;
      }

      .billing-print-table {
        border-collapse: collapse;
        page-break-inside: auto;
        width: 100%;
      }

      .billing-print-table th,
      .billing-print-table td {
        border-bottom: 1px solid #d1d5db;
        padding: 5px 6px;
        text-align: left;
        vertical-align: top;
      }

      .billing-print-table th {
        background: #f3f4f6 !important;
        color: #374151;
        font-size: 8pt;
        font-weight: 700;
        text-transform: uppercase;
      }

      .billing-print-table tr {
        break-inside: avoid;
      }

      .billing-print-right {
        text-align: right !important;
      }

      .billing-print-muted {
        color: #6b7280;
      }

      .billing-print-digest {
        border-top: 1px solid #d1d5db;
        color: #4b5563;
        font-size: 7.5pt;
        margin-top: 18px;
        padding-top: 8px;
        word-break: break-all;
      }
    }
  `}</style>
);

const PrintSummaryCard: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div className="billing-print-card">
    <div className="billing-print-card-label">{label}</div>
    <div className="billing-print-card-value">{value}</div>
    {note && <div className="billing-print-muted">{note}</div>}
  </div>
);

const PrintSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="billing-print-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const OfficialBillingReportDocument: React.FC<{ payload: BillingReportPrintPayload }> = ({ payload }) => {
  const { reportType, reportLabel, reportData, generatedAt, generatedBy, reportPeriod, digest } = payload;
  return (
    <article data-billing-report-print-document>
      <header className="billing-print-header">
        <div className="billing-print-kicker">EdgeLex Official Billing Report</div>
        <div className="billing-print-title">{reportLabel}</div>
        <div className="billing-print-meta">
          <div><span>Period</span>{reportPeriod}</div>
          <div><span>Generated</span>{new Date(generatedAt).toLocaleString()}</div>
          <div><span>Generated by</span>{generatedBy}</div>
          <div><span>Report type</span>{reportType}</div>
        </div>
      </header>

      {reportType === 'ar-aging' && <PrintableArAgingReport data={reportData} />}
      {reportType === 'wip-aging' && <PrintableWipAgingReport data={reportData} />}
      {reportType === 'expense-recovery' && <PrintableExpenseRecoveryReport data={reportData} />}
      {reportType === 'realization' && <PrintableRealizationReport data={reportData} />}
      {reportType === 'utilization' && <PrintableUtilizationReport data={reportData} />}

      <footer className="billing-print-digest">
        SHA-256 report digest: {digest}
      </footer>
    </article>
  );
};

const PrintableArAgingReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <PrintSection title="Aging Summary">
      <div className="billing-print-summary">
        {AGING_BUCKETS.map(({ key, label }) => (
          <PrintSummaryCard key={key} label={label} value={formatCurrency(data.summary[key])} />
        ))}
        <PrintSummaryCard label="Total Outstanding" value={formatCurrency(data.summary.total)} />
      </div>
    </PrintSection>
    <PrintSection title="Client Detail">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Client</th>
            {AGING_BUCKETS.map(({ key, label }) => <th key={key} className="billing-print-right">{label}</th>)}
            <th className="billing-print-right">Total</th>
            <th className="billing-print-right">Bills</th>
            <th>Oldest</th>
          </tr>
        </thead>
        <tbody>
          {data.by_client.map((row: any) => (
            <tr key={row.client_id}>
              <td>{row.client_name}</td>
              {AGING_BUCKETS.map(({ key }) => <td key={key} className="billing-print-right">{row[key] > 0 ? formatCurrency(row[key]) : '-'}</td>)}
              <td className="billing-print-right">{formatCurrency(row.total)}</td>
              <td className="billing-print-right">{row.bill_count}</td>
              <td>{formatDate(row.oldest_invoice_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  </>
);

const PrintableWipAgingReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <PrintSection title="WIP Summary">
      <div className="billing-print-summary">
        {AGING_BUCKETS.map(({ key, label }) => (
          <PrintSummaryCard key={key} label={label} value={formatCurrency(data.summary[key])} />
        ))}
        <PrintSummaryCard label="Total WIP" value={formatCurrency(data.summary.total)} />
      </div>
    </PrintSection>
    <PrintSection title="Matter Detail">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Matter</th>
            <th>Client</th>
            <th className="billing-print-right">Time WIP</th>
            <th className="billing-print-right">Expense WIP</th>
            <th className="billing-print-right">Total</th>
            <th className="billing-print-right">Entries</th>
          </tr>
        </thead>
        <tbody>
          {data.by_matter.map((row: any) => (
            <tr key={row.matter_id}>
              <td>{row.matter_number || '-'}<br /><span className="billing-print-muted">{row.matter_description}</span></td>
              <td>{row.client_name}</td>
              <td className="billing-print-right">{formatCurrency(row.time_wip)}</td>
              <td className="billing-print-right">{formatCurrency(row.expense_wip)}</td>
              <td className="billing-print-right">{formatCurrency(row.total)}</td>
              <td className="billing-print-right">{row.entry_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  </>
);

const PrintableExpenseRecoveryReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <PrintSection title="Expense Recovery Summary">
      <div className="billing-print-summary">
        <PrintSummaryCard label="Billable Costs" value={formatCurrency(data.summary.billable_amount)} />
        <PrintSummaryCard label="Draft / Prebill" value={formatCurrency(data.summary.prebill_amount)} />
        <PrintSummaryCard label="Invoice AR" value={formatCurrency(data.summary.ar_amount)} />
        <PrintSummaryCard label="Unbilled / Ready" value={formatCurrency(data.summary.unbilled_amount + data.summary.ready_to_bill_amount)} />
        <PrintSummaryCard label="Print / Copy" value={formatCurrency(data.summary.print_copy_amount)} note={`${data.summary.print_copy_pages.toLocaleString()} pages`} />
        <PrintSummaryCard label="All Expenses" value={formatCurrency(data.summary.total_expenses)} note={`${data.summary.expense_count} items`} />
      </div>
    </PrintSection>
    <PrintSection title="By UTBMS Expense Code">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Category</th>
            <th className="billing-print-right">Amount</th>
            <th className="billing-print-right">Prebill</th>
            <th className="billing-print-right">AR</th>
            <th className="billing-print-right">Items</th>
          </tr>
        </thead>
        <tbody>
          {data.by_utbms.map((row: any) => (
            <tr key={`${row.utbms_expense_code}-${row.expense_category}`}>
              <td>{row.utbms_expense_code}</td>
              <td>{row.description}</td>
              <td className="billing-print-right">{formatCurrency(row.amount)}</td>
              <td className="billing-print-right">{row.prebill_amount > 0 ? formatCurrency(row.prebill_amount) : '-'}</td>
              <td className="billing-print-right">{row.ar_amount > 0 ? formatCurrency(row.ar_amount) : '-'}</td>
              <td className="billing-print-right">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
    <PrintSection title="By Client / Matter">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Matter</th>
            <th>Client</th>
            <th className="billing-print-right">Amount</th>
            <th className="billing-print-right">Print / Copy</th>
            <th className="billing-print-right">Prebill</th>
            <th className="billing-print-right">AR</th>
            <th className="billing-print-right">Items</th>
          </tr>
        </thead>
        <tbody>
          {data.by_matter.map((row: any) => (
            <tr key={row.matter_id}>
              <td>{row.matter_number || '-'}</td>
              <td>{row.client_name}</td>
              <td className="billing-print-right">{formatCurrency(row.amount)}</td>
              <td className="billing-print-right">
                {row.print_copy_amount > 0 ? formatCurrency(row.print_copy_amount) : '-'}
                {row.print_copy_pages > 0 && <><br /><span className="billing-print-muted">{row.print_copy_pages.toLocaleString()} pages</span></>}
              </td>
              <td className="billing-print-right">{row.prebill_amount > 0 ? formatCurrency(row.prebill_amount) : '-'}</td>
              <td className="billing-print-right">{row.ar_amount > 0 ? formatCurrency(row.ar_amount) : '-'}</td>
              <td className="billing-print-right">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
    <PrintSection title="Recent Expense Items">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Matter</th>
            <th>State</th>
            <th>Invoice</th>
            <th className="billing-print-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {data.recent_items.map((row: any) => (
            <tr key={row.expense_id}>
              <td>{formatDate(row.expense_date)}</td>
              <td>{row.description}<br /><span className="billing-print-muted">{row.utbms_expense_code} - {row.quantity} {row.unit_type || 'unit'} @ {formatCurrency(row.unit_cost)}</span></td>
              <td>{row.matter_number || '-'}<br /><span className="billing-print-muted">{row.client_name}</span></td>
              <td>{row.billing_state}</td>
              <td>{row.bill_number || '-'}</td>
              <td className="billing-print-right">{formatCurrency(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  </>
);

const PrintableRealizationReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <PrintSection title="Realization Summary">
      <div className="billing-print-summary">
        <PrintSummaryCard label="Standard Value" value={formatCurrency(data.overall.standard_value)} />
        <PrintSummaryCard label="Billed Value" value={formatCurrency(data.overall.billed_value)} />
        <PrintSummaryCard label="Collected" value={formatCurrency(data.overall.collected_value)} />
        <PrintSummaryCard label="Realization Rate" value={formatPercent(data.overall.realization_rate)} />
        <PrintSummaryCard label="Collection Rate" value={formatPercent(data.overall.collection_rate)} />
      </div>
    </PrintSection>
    <PrintSection title="Attorney Detail">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Attorney</th>
            <th className="billing-print-right">Hours</th>
            <th className="billing-print-right">Standard</th>
            <th className="billing-print-right">Billed</th>
            <th className="billing-print-right">Write-Offs</th>
            <th className="billing-print-right">Realization</th>
          </tr>
        </thead>
        <tbody>
          {data.by_attorney.map((row: any) => (
            <tr key={row.user_id}>
              <td>{row.user_name}</td>
              <td className="billing-print-right">{row.hours_billed.toFixed(1)}</td>
              <td className="billing-print-right">{formatCurrency(row.standard_value)}</td>
              <td className="billing-print-right">{formatCurrency(row.billed_value)}</td>
              <td className="billing-print-right">{row.write_off_amount > 0 ? formatCurrency(row.write_off_amount) : '-'}</td>
              <td className="billing-print-right">{formatPercent(row.realization_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  </>
);

const PrintableUtilizationReport: React.FC<{ data: any }> = ({ data }) => (
  <>
    <PrintSection title="Utilization Summary">
      <div className="billing-print-summary">
        <PrintSummaryCard label="Total Hours" value={data.overall.total_hours.toFixed(1)} />
        <PrintSummaryCard label="Billable Hours" value={data.overall.billable_hours.toFixed(1)} />
        <PrintSummaryCard label="Non-Billable Hours" value={data.overall.non_billable_hours.toFixed(1)} />
        <PrintSummaryCard label="Utilization Rate" value={formatPercent(data.overall.utilization_rate)} />
      </div>
    </PrintSection>
    <PrintSection title="Attorney Detail">
      <table className="billing-print-table">
        <thead>
          <tr>
            <th>Attorney</th>
            <th className="billing-print-right">Billable</th>
            <th className="billing-print-right">Non-Billable</th>
            <th className="billing-print-right">Total</th>
            <th className="billing-print-right">Target</th>
            <th className="billing-print-right">Variance</th>
            <th className="billing-print-right">Utilization</th>
          </tr>
        </thead>
        <tbody>
          {data.by_attorney.map((row: any) => (
            <tr key={row.user_id}>
              <td>{row.user_name}</td>
              <td className="billing-print-right">{row.billable_hours.toFixed(1)}</td>
              <td className="billing-print-right">{row.non_billable_hours.toFixed(1)}</td>
              <td className="billing-print-right">{row.total_hours.toFixed(1)}</td>
              <td className="billing-print-right">{row.target_hours.toFixed(0)}</td>
              <td className="billing-print-right">{row.variance >= 0 ? '+' : ''}{row.variance.toFixed(1)}</td>
              <td className="billing-print-right">{formatPercent(row.utilization_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PrintSection>
  </>
);

// ── AR Aging Report View ──

const ArAgingReportView: React.FC<{ data: any }> = ({ data }) => {
  const { summary, by_client, as_of_date } = data;
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 dark:text-gray-400">As of {formatDate(as_of_date)}</div>

      {/* Summary buckets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {AGING_BUCKETS.map(({ key, label }) => (
          <div key={key} className={`p-3 rounded-lg border border-gray-200 dark:border-gray-700 ${getBucketColor(key)}`}>
            <div className="text-xs font-medium opacity-75">{label}</div>
            <div className="text-lg font-bold mt-1">{formatCurrency(summary[key])}</div>
          </div>
        ))}
        <div className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-800">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Total Outstanding</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(summary.total)}</div>
        </div>
      </div>

      {/* Client breakdown table */}
      {by_client.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Client</th>
                {AGING_BUCKETS.map(({ key, label }) => (
                  <th key={key} className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">{label.split(' ')[0]}</th>
                ))}
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Total</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Bills</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Oldest</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_client.map((row: any) => (
                <tr key={row.client_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{row.client_name}</td>
                  {AGING_BUCKETS.map(({ key }) => (
                    <td key={key} className={`px-3 py-2 text-right ${row[key] > 0 ? getBucketColor(key).split(' ')[0] : 'text-gray-400 dark:text-gray-500'}`}>
                      {row[key] > 0 ? formatCurrency(row[key]) : '-'}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-bold text-gray-900 dark:text-white">{formatCurrency(row.total)}</td>
                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.bill_count}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDate(row.oldest_invoice_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {by_client.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No outstanding receivables found. Draft invoices and unbilled expenses appear in WIP Aging or Expense Recovery until the invoice is sent.
        </div>
      )}
    </div>
  );
};

// ── WIP Aging Report View ──

const WipAgingReportView: React.FC<{ data: any }> = ({ data }) => {
  const { summary, by_matter, as_of_date } = data;
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 dark:text-gray-400">As of {formatDate(as_of_date)}</div>

      {/* Summary buckets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {AGING_BUCKETS.map(({ key, label }) => (
          <div key={key} className={`p-3 rounded-lg border border-gray-200 dark:border-gray-700 ${getBucketColor(key)}`}>
            <div className="text-xs font-medium opacity-75">{label}</div>
            <div className="text-lg font-bold mt-1">{formatCurrency(summary[key])}</div>
          </div>
        ))}
        <div className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-800">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Total WIP</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(summary.total)}</div>
        </div>
      </div>

      {/* Matter breakdown table */}
      {by_matter.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Client</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Time WIP</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Expense WIP</th>
                {AGING_BUCKETS.map(({ key, label }) => (
                  <th key={key} className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">{label.split(' ')[0]}</th>
                ))}
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Total</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_matter.map((row: any) => (
                <tr key={row.matter_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white">
                    <div className="font-medium">{row.matter_number || '-'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{row.matter_description}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.client_name}</td>
                  <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{formatCurrency(row.time_wip)}</td>
                  <td className="px-3 py-2 text-right text-amber-600 dark:text-amber-400">{formatCurrency(row.expense_wip)}</td>
                  {AGING_BUCKETS.map(({ key }) => (
                    <td key={key} className={`px-3 py-2 text-right ${row[key] > 0 ? getBucketColor(key).split(' ')[0] : 'text-gray-400 dark:text-gray-500'}`}>
                      {row[key] > 0 ? formatCurrency(row[key]) : '-'}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-bold text-gray-900 dark:text-white">{formatCurrency(row.total)}</td>
                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.entry_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {by_matter.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No unbilled WIP found.</div>
      )}
    </div>
  );
};

// ── Expense Recovery Report View ──

const ExpenseRecoveryReportView: React.FC<{ data: any }> = ({ data }) => {
  const { summary, by_utbms, by_matter, recent_items, date_from, date_to } = data;

  return (
    <div className="space-y-5">
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Period: {formatDate(date_from)} — {formatDate(date_to)}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-lex-accent-soft">
          <div className="text-xs font-medium text-lex-accent">Billable Costs</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(summary.billable_amount)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
          <div className="text-xs font-medium text-blue-700 dark:text-blue-300">Draft / Prebill</div>
          <div className="text-lg font-bold text-blue-900 dark:text-blue-100 mt-1">{formatCurrency(summary.prebill_amount)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
          <div className="text-xs font-medium text-green-700 dark:text-green-300">Invoice AR</div>
          <div className="text-lg font-bold text-green-900 dark:text-green-100 mt-1">{formatCurrency(summary.ar_amount)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
          <div className="text-xs font-medium text-amber-700 dark:text-amber-300">Unbilled / Ready</div>
          <div className="text-lg font-bold text-amber-900 dark:text-amber-100 mt-1">
            {formatCurrency(summary.unbilled_amount + summary.ready_to_bill_amount)}
          </div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20">
          <div className="text-xs font-medium text-purple-700 dark:text-purple-300">Print / Copy</div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100 mt-1">{formatCurrency(summary.print_copy_amount)}</div>
          <div className="text-xs text-purple-700/80 dark:text-purple-300/80">{summary.print_copy_pages.toLocaleString()} pages</div>
        </div>
        <div className="p-3 rounded-lg border-2 border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-800">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">All Expenses</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(summary.total_expenses)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{summary.expense_count} items</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">By UTBMS Expense Code</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Code</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Prebill</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">AR</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Items</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_utbms.map((row: any) => (
                <tr key={`${row.utbms_expense_code}-${row.expense_category}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{row.utbms_expense_code}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    <div className="font-medium">{row.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{String(row.expense_category).replace(/_/g, ' ')}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-semibold">{formatCurrency(row.amount)}</td>
                  <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{row.prebill_amount > 0 ? formatCurrency(row.prebill_amount) : '-'}</td>
                  <td className="px-3 py-2 text-right text-green-600 dark:text-green-400">{row.ar_amount > 0 ? formatCurrency(row.ar_amount) : '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {by_utbms.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No expenses found for this period.</div>
          )}
        </div>

        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">By Client / Matter</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Print/Copy</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Prebill</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Items</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_matter.map((row: any) => (
                <tr key={row.matter_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white">
                    <div className="font-medium">{row.matter_number || '-'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{row.client_name}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-semibold">{formatCurrency(row.amount)}</td>
                  <td className="px-3 py-2 text-right text-purple-600 dark:text-purple-400">
                    {row.print_copy_amount > 0 ? formatCurrency(row.print_copy_amount) : '-'}
                    {row.print_copy_pages > 0 && <div className="text-xs">{row.print_copy_pages.toLocaleString()} pages</div>}
                  </td>
                  <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{row.prebill_amount > 0 ? formatCurrency(row.prebill_amount) : '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {by_matter.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No matter expense activity found.</div>
          )}
        </div>
      </div>

      {recent_items.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Expense Items</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">State</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Invoice</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {recent_items.map((row: any) => (
                <tr key={row.expense_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDate(row.expense_date)}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white">
                    <div className="font-medium truncate max-w-sm">{row.description}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{row.utbms_expense_code} · {row.quantity} {row.unit_type || 'unit'} @ {formatCurrency(row.unit_cost)}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    <div>{row.matter_number || '-'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{row.client_name}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.billing_state}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.bill_number || '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-semibold">{formatCurrency(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Realization Report View ──

const RealizationReportView: React.FC<{ data: any }> = ({ data }) => {
  const { overall, by_attorney, date_from, date_to } = data;
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Period: {formatDate(date_from)} — {formatDate(date_to)}
      </div>

      {/* Overall metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-lex-accent-soft">
          <div className="text-xs font-medium text-lex-accent">Standard Value</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{formatCurrency(overall.standard_value)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
          <div className="text-xs font-medium text-green-600 dark:text-green-400">Billed Value</div>
          <div className="text-lg font-bold text-green-900 dark:text-green-100 mt-1">{formatCurrency(overall.billed_value)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/20">
          <div className="text-xs font-medium text-purple-600 dark:text-purple-400">Collected</div>
          <div className="text-lg font-bold text-purple-900 dark:text-purple-100 mt-1">{formatCurrency(overall.collected_value)}</div>
        </div>
        <div className={`p-3 rounded-lg border-2 ${overall.realization_rate >= 0.9 ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : overall.realization_rate >= 0.7 ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Realization Rate</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatPercent(overall.realization_rate)}</div>
        </div>
        <div className={`p-3 rounded-lg border-2 ${overall.collection_rate >= 0.85 ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : overall.collection_rate >= 0.6 ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Collection Rate</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatPercent(overall.collection_rate)}</div>
        </div>
      </div>

      {/* Attorney breakdown */}
      {by_attorney.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Attorney</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Hours Billed</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Standard Value</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Billed Value</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Write-Offs</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Realization</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_attorney.map((row: any) => (
                <tr key={row.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{row.user_name}</td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{row.hours_billed.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(row.standard_value)}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium">{formatCurrency(row.billed_value)}</td>
                  <td className="px-3 py-2 text-right text-red-600 dark:text-red-400">{row.write_off_amount > 0 ? formatCurrency(row.write_off_amount) : '-'}</td>
                  <td className={`px-3 py-2 text-right font-bold ${row.realization_rate >= 0.9 ? 'text-green-600 dark:text-green-400' : row.realization_rate >= 0.7 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatPercent(row.realization_rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {by_attorney.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No billing data found for this period.</div>
      )}
    </div>
  );
};

// ── Utilization Report View ──

const UtilizationReportView: React.FC<{ data: any }> = ({ data }) => {
  const { overall, by_attorney, date_from, date_to } = data;
  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Period: {formatDate(date_from)} — {formatDate(date_to)}
      </div>

      {/* Overall metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-lex-accent-soft">
          <div className="text-xs font-medium text-lex-accent">Total Hours</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">{overall.total_hours.toFixed(1)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
          <div className="text-xs font-medium text-green-600 dark:text-green-400">Billable Hours</div>
          <div className="text-lg font-bold text-green-900 dark:text-green-100 mt-1">{overall.billable_hours.toFixed(1)}</div>
        </div>
        <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
          <div className="text-xs font-medium text-amber-600 dark:text-amber-400">Non-Billable</div>
          <div className="text-lg font-bold text-amber-900 dark:text-amber-100 mt-1">{overall.non_billable_hours.toFixed(1)}</div>
        </div>
        <div className={`p-3 rounded-lg border-2 ${overall.utilization_rate >= 0.8 ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : overall.utilization_rate >= 0.6 ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' : 'border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">Utilization Rate</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatPercent(overall.utilization_rate)}</div>
        </div>
      </div>

      {/* Attorney breakdown */}
      {by_attorney.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Attorney</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Billable</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Non-Billable</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Total</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Target</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Variance</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Utilization</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium w-40">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {by_attorney.map((row: any) => {
                const pct = Math.min(100, (row.utilization_rate * 100));
                const barColor = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500';
                return (
                  <tr key={row.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">{row.user_name}</td>
                    <td className="px-3 py-2 text-right text-green-600 dark:text-green-400">{row.billable_hours.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right text-amber-600 dark:text-amber-400">{row.non_billable_hours.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right text-gray-900 dark:text-white">{row.total_hours.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{row.target_hours.toFixed(0)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${row.variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {row.variance >= 0 ? '+' : ''}{row.variance.toFixed(1)}
                    </td>
                    <td className={`px-3 py-2 text-right font-bold ${row.utilization_rate >= 0.8 ? 'text-green-600 dark:text-green-400' : row.utilization_rate >= 0.6 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                      {formatPercent(row.utilization_rate)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {by_attorney.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">No time entries found for this period.</div>
      )}
    </div>
  );
};

export default BillingReportsPage;
